import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { useAccount, usePublicClient, useWalletClient } from 'wagmi';
import { useWeb3Modal } from '@web3modal/wagmi/react';
import { formatUnits } from 'viem';
import { useQueryClient } from '@tanstack/react-query';
import { usePoolLength, useStakingTotals, useUserPortfolio, useTokenBalance, usePoolInfo, useLRSTPrice } from '../hooks/useStaking';
import { LRST_TOKEN, STAKING_CONTRACT, MASTERCHEF_ABI } from '../config/staking';
import { formatErrorForToast } from '../utils/errorMessages';
import StakingPoolCard from '../components/StakingPoolCard';
import WalletModal from '../components/WalletModal';
import { SkeletonPoolCard } from '../components/Skeleton/Skeleton';
import Tooltip from '../components/Tooltip/Tooltip';
import EmptyState from '../components/EmptyState/EmptyState';
import { toast } from '../utils/toast';
import '../App.css';

const Staking = () => {
  const { address, isConnected } = useAccount();
  const { open } = useWeb3Modal();
  const publicClient = usePublicClient();
  const { data: walletClient } = useWalletClient();
  const queryClient = useQueryClient();
  
  const { data: poolLength, isLoading: loadingPools } = usePoolLength();
  const { data: totals } = useStakingTotals();
  const { data: portfolio } = useUserPortfolio(isConnected ? address : null);
  const { data: lrstBalance } = useTokenBalance(LRST_TOKEN, isConnected ? address : null);
  const { data: lrstPrice } = useLRSTPrice(); // Get LRST price in USD
  
  // Get LP token address from pool #1 for LP balance
  const { data: lpPoolInfo } = usePoolInfo(1);
  const { data: lpBalance } = useTokenBalance(lpPoolInfo?.lpToken, isConnected ? address : null);
  
  // Animation states
  const [animationComplete, setAnimationComplete] = useState(false);
  const [displayTVL, setDisplayTVL] = useState(0);
  const [displayTotalStaked, setDisplayTotalStaked] = useState(0);
  const [displayTotalRewards, setDisplayTotalRewards] = useState(0);
  const [displayLrstBalance, setDisplayLrstBalance] = useState(0);
  const [displayLpBalance, setDisplayLpBalance] = useState(0);
  const [displayTotalDeposits, setDisplayTotalDeposits] = useState(0);
  const [displayTotalLPDeposits, setDisplayTotalLPDeposits] = useState(0);
  const [displayPendingRewards, setDisplayPendingRewards] = useState(0);

  // Wait for animation to complete before starting counter animations
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationComplete(true);
    }, 1200); // Match fade-in animation timing
    return () => clearTimeout(timer);
  }, []);

  // Counter animation helper function
  const animateValue = (targetValue, setter) => {
    const duration = 1500;
    const steps = 60;
    const stepDuration = duration / steps;
    
    let currentStep = 0;
    const interval = setInterval(() => {
      currentStep++;
      const progress = currentStep / steps;
      const easeProgress = 1 - Math.pow(1 - progress, 3); // Ease-out cubic
      
      setter(targetValue * easeProgress);
      
      if (currentStep >= steps) {
        clearInterval(interval);
        setter(targetValue);
      }
    }, stepDuration);
    
    return interval;
  };

  // Animate TVL
  useEffect(() => {
    if (!animationComplete || !totals || !totals.tvlUsd) return;
    const value = totals.tvlUsd;
    const interval = animateValue(value, setDisplayTVL);
    return () => clearInterval(interval);
  }, [totals, animationComplete]);

  // Animate Total Staked
  useEffect(() => {
    if (!animationComplete || !totals) return;
    const value = parseFloat(formatUnits(totals.totalStaked, 18));
    const interval = animateValue(value, setDisplayTotalStaked);
    return () => clearInterval(interval);
  }, [totals, animationComplete]);

  // Animate Total Rewards
  useEffect(() => {
    if (!animationComplete || !totals) return;
    const value = parseFloat(formatUnits(totals.totalRewards, 18));
    const interval = animateValue(value, setDisplayTotalRewards);
    return () => clearInterval(interval);
  }, [totals, animationComplete]);

  // Animate LRST Balance
  useEffect(() => {
    if (!animationComplete || !isConnected || !lrstBalance) return;
    const value = parseFloat(formatUnits(lrstBalance, 18));
    const interval = animateValue(value, setDisplayLrstBalance);
    return () => clearInterval(interval);
  }, [lrstBalance, animationComplete, isConnected]);

  // Animate LP Balance
  useEffect(() => {
    if (!animationComplete || !isConnected || !lpBalance || !lpPoolInfo) return;
    const value = parseFloat(formatUnits(lpBalance, lpPoolInfo.tokenInfo?.decimals || 18));
    const interval = animateValue(value, setDisplayLpBalance);
    return () => clearInterval(interval);
  }, [lpBalance, lpPoolInfo, animationComplete, isConnected]);

  // Animate Total Deposits
  useEffect(() => {
    if (!animationComplete || !isConnected || !portfolio) return;
    const value = parseFloat(formatUnits(portfolio.totalDeposits, 18));
    const interval = animateValue(value, setDisplayTotalDeposits);
    return () => clearInterval(interval);
  }, [portfolio, animationComplete, isConnected]);

  // Animate Total LP Deposits
  useEffect(() => {
    if (!animationComplete || !isConnected || !portfolio) return;
    const value = parseFloat(formatUnits(portfolio.totalLPDeposits, 18));
    const interval = animateValue(value, setDisplayTotalLPDeposits);
    return () => clearInterval(interval);
  }, [portfolio, animationComplete, isConnected]);

  // Animate Pending Rewards
  useEffect(() => {
    if (!animationComplete || !isConnected || !portfolio) return;
    const value = parseFloat(formatUnits(portfolio.totalPendingRewards, 18));
    const interval = animateValue(value, setDisplayPendingRewards);
    return () => clearInterval(interval);
  }, [portfolio, animationComplete, isConnected]);

  const handleConnectWallet = () => {
    open();
  };

  const shortAddress = (addr) => {
    if (!addr) return '';
    return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
  };

  return (
    <>
      <Helmet>
        <title>Little Rabbit Staking</title>
        <link rel="icon" type="image/png" href="/favicon-round.png?v=4" />
        <link rel="shortcut icon" href="/favicon-round.png?v=4" />
      </Helmet>
      <motion.div 
        className="staking-page"
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
      >
      {/* Totals Section */}
      <motion.div 
        className="staking-totals"
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
      >
        <motion.div 
          className="staking-total-card"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="staking-total-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="14" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
            </svg>
          </div>
          <div>
            <Tooltip content="Total Value Locked represents the total USD value of all LRST tokens staked across all pools" position="bottom">
              <p className="staking-total-label" style={{ cursor: 'help', borderBottom: '1px dotted rgba(255,255,255,0.3)' }}>
                Total Value Locked
              </p>
            </Tooltip>
            <h3 className="staking-total-value">
              {animationComplete && totals && totals.tvlUsd !== undefined
                ? `$${Math.round(displayTVL).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : '$0'
              }
            </h3>
          </div>
        </motion.div>

        <motion.div 
          className="staking-total-card"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="staking-total-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 2L2 7l10 5 10-5-10-5z" />
              <path d="M2 17l10 5 10-5" />
              <path d="M2 12l10 5 10-5" />
            </svg>
          </div>
          <div>
            <Tooltip content="Total amount of LRST tokens currently staked by all users" position="bottom">
              <p className="staking-total-label" style={{ cursor: 'help', borderBottom: '1px dotted rgba(255,255,255,0.3)' }}>
                Total Staked
              </p>
            </Tooltip>
            <h3 className="staking-total-value">
              {animationComplete && totals 
                ? `${displayTotalStaked.toLocaleString(undefined, { maximumFractionDigits: 0 })} LRST`
                : '0 LRST'
              }
            </h3>
            <p className="staking-total-subtitle" style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
              {totals && lrstPrice 
                ? `≈ $${Math.round(parseFloat(formatUnits(totals.totalStaked, 18)) * lrstPrice).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : '$0'
              }
            </p>
          </div>
        </motion.div>

        <motion.div 
          className="staking-total-card"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <div className="staking-total-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="8" r="6" />
              <path d="M15.477 12.89L17 22l-5-3-5 3 1.523-9.11" />
            </svg>
          </div>
          <div>
            <Tooltip content="Total rewards distributed to all stakers across all pools" position="bottom">
              <p className="staking-total-label" style={{ cursor: 'help', borderBottom: '1px dotted rgba(255,255,255,0.3)' }}>
                Rewards Distributed
              </p>
            </Tooltip>
            <h3 className="staking-total-value">
              {animationComplete && totals 
                ? displayTotalRewards.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : '0'
              } LRST
            </h3>
            <p className="staking-total-subtitle" style={{ fontSize: '12px', opacity: 0.7, marginTop: '4px' }}>
              {totals && lrstPrice 
                ? `≈ $${Math.round(parseFloat(formatUnits(totals.totalRewards, 18)) * lrstPrice).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : '$0'
              }
            </p>
          </div>
        </motion.div>
      </motion.div>

      {/* Portfolio Section */}
      <motion.div 
        className="staking-portfolio"
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.5 }}
      >
        <div className="staking-portfolio-header">
          <h2 className="staking-section-title">Your Portfolio</h2>
          <span className="staking-wallet-address">
            {isConnected ? shortAddress(address) : 'Not Connected'}
          </span>
        </div>

        <div className="staking-portfolio-stats">
          {/* Hidden: LRST Wallet Balance
          <div className="staking-portfolio-stat staking-portfolio-stat-boxed">
            <span className="staking-portfolio-label">LRST Wallet Balance</span>
            <span className="staking-portfolio-value staking-highlight">
              {animationComplete && isConnected && lrstBalance 
                ? displayLrstBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : '0'
              } LRST
            </span>
          </div>
          */}

          {/* Hidden: LP Wallet Balance
          <div className="staking-portfolio-stat staking-portfolio-stat-boxed">
            <span className="staking-portfolio-label">LP Wallet Balance</span>
            <span className="staking-portfolio-value staking-highlight">
              {animationComplete && isConnected && lpPoolInfo && lpBalance
                ? displayLpBalance.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : '0'
              } LRST BNB-LP
            </span>
          </div>
          */}

          <div className="staking-portfolio-stat staking-portfolio-stat-boxed">
            <span className="staking-portfolio-label">Total Token Deposit</span>
            <span className="staking-portfolio-value staking-highlight">
              {animationComplete && isConnected && portfolio
                ? displayTotalDeposits.toLocaleString(undefined, { maximumFractionDigits: 0 })
                : '0'
              } LRST
            </span>
          </div>

          <div className="staking-portfolio-stat staking-portfolio-stat-boxed">
            <span className="staking-portfolio-label">Total LP Deposits</span>
            <span className="staking-portfolio-value staking-highlight">
              {animationComplete && isConnected && portfolio
                ? displayTotalLPDeposits.toLocaleString(undefined, { minimumFractionDigits: 6, maximumFractionDigits: 6 })
                : '0.000000'
              } LRST BNB-LP
            </span>
          </div>

          <div className="staking-portfolio-stat staking-portfolio-stat-boxed">
            <span className="staking-portfolio-label">Pending Rewards</span>
            <span className="staking-portfolio-value staking-highlight">
              {animationComplete && isConnected && portfolio
                ? displayPendingRewards.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 6 })
                : '0.00'
              } LRST
            </span>
          </div>
        </div>
      </motion.div>

      {/* Pools Section */}
      <motion.div 
        className="staking-pools-section"
        initial={{ opacity: 0, y: 80 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.6 }}
      >
        <h2 className="staking-section-title">Staking Pools</h2>
        
        {loadingPools ? (
          <div className="staking-pools-grid">
            <SkeletonPoolCard />
            <SkeletonPoolCard />
            <SkeletonPoolCard />
          </div>
        ) : poolLength > 0 ? (
          <div className="staking-pools-grid">
            {Array.from({ length: poolLength }, (_, i) => (
              <StakingPoolCard 
                key={i} 
                pid={i} 
                onConnectWallet={handleConnectWallet}
              />
            ))}
          </div>
        ) : (
          <EmptyState
            icon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
              </svg>
            }
            title="No Staking Pools Available"
            description="Start staking to earn rewards. Check back soon for new pools!"
          />
        )}
      </motion.div>
    </motion.div>
    </>
  );
};

export default Staking;
